import { HttpClient } from '@angular/common/http';
import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

export interface FoxpostDialogData {
  orderId: number;
  reference?: string;
  replacement?: boolean;
  replacementId?: number;
}

export interface FoxpostDialogResult {
  response: any;
}

interface FoxpostApm {
  id: string;
  name: string;
  address: string;
  findme?: string;
}

interface FoxpostDraft {
  replacement: boolean;
  replacementId?: number | null;
  mode: 'home' | 'apm';
  recipient: { name: string; phone: string; email: string; zip: string; city: string; address: string };
  apm?: FoxpostApm | null;
  size: string;
  cod: number;
  comment: string;
  deliveryNote: string;
  fragile: boolean;
}

interface FoxpostDraftResponse {
  configured: boolean;
  environment: string;
  sizes: string[];
  warnings: string[];
  existingLabel: { provider: string | null; trackingCode: string | null } | null;
  draft: FoxpostDraft;
}

/**
 * Foxpost címke (belföld): házhozszállítás a rendelés címére vagy automata
 * kereséssel, méret, utánvét. A UPS-dialógus stílusát (lbl__*) használja.
 */
@Component({
  selector: 'app-foxpost-dialog',
  templateUrl: './foxpost-dialog.component.html',
  // a UPS-dialógus lbl__* stílusai + a Foxpost saját elemei
  styleUrls: ['../label-dialog/label-dialog.component.scss', './foxpost-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FoxpostDialogComponent {
  loading = true;
  busy = false;
  error = '';
  info: FoxpostDraftResponse | null = null;
  draft: FoxpostDraft | null = null;

  apmQuery = '';
  apmResults: FoxpostApm[] = [];
  apmSearching = false;
  private apmSearch$ = new Subject<string>();

  readonly sizeLabels: Record<string, string> = {
    xs: 'XS – boríték',
    s: 'S – kicsi',
    m: 'M – közepes',
    l: 'L – nagy',
    xl: 'XL – extra nagy',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FoxpostDialogData,
    private dialogRef: MatDialogRef<FoxpostDialogComponent, FoxpostDialogResult>,
    private http: HttpClient,
  ) {
    const params: Record<string, string> = {};
    if (data.replacement) params['replacement'] = '1';
    if (data.replacementId) params['replacementId'] = `${data.replacementId}`;
    this.http.get<FoxpostDraftResponse>(`orders/${data.orderId}/foxpost/draft`, { params }).subscribe({
      next: (info) => {
        this.loading = false;
        this.info = info;
        this.draft = info.draft;
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message || err?.message || 'A Foxpost adatok betöltése sikertelen.';
      },
    });
    this.apmSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((q) => {
          this.apmSearching = true;
          return this.http.get<FoxpostApm[]>('integrations/foxpost/apms', { params: { q } });
        }),
      )
      .subscribe({
        next: (list) => {
          this.apmSearching = false;
          this.apmResults = list || [];
        },
        error: () => {
          this.apmSearching = false;
          this.apmResults = [];
        },
      });
  }

  get title(): string {
    return this.data.replacement ? 'Foxpost címke a csere csomaghoz' : 'Foxpost címke létrehozása';
  }

  get canCreate(): boolean {
    const d = this.draft;
    if (!d || !this.info?.configured || this.busy) return false;
    const r = d.recipient;
    if (!r.name?.trim() || !r.phone?.trim() || !r.email?.trim()) return false;
    return d.mode === 'apm' ? !!d.apm?.id : !!(r.zip?.trim() && r.city?.trim() && r.address?.trim());
  }

  setMode(mode: 'home' | 'apm') {
    if (this.draft) this.draft.mode = mode;
  }

  onApmQuery(value: string) {
    this.apmQuery = value;
    if (value.trim().length >= 2) this.apmSearch$.next(value.trim());
    else this.apmResults = [];
  }

  pickApm(apm: FoxpostApm) {
    if (!this.draft) return;
    this.draft.apm = apm;
    this.apmResults = [];
    this.apmQuery = '';
  }

  create() {
    if (!this.draft || !this.canCreate) return;
    this.busy = true;
    this.error = '';
    this.http.post(`orders/${this.data.orderId}/foxpost/label`, { draft: this.draft }).subscribe({
      next: (response) => this.dialogRef.close({ response }),
      error: (err) => {
        this.busy = false;
        this.error = err?.error?.message || err?.message || 'A Foxpost címke létrehozása sikertelen.';
      },
    });
  }

  cancel() {
    this.dialogRef.close();
  }
}

@Injectable({ providedIn: 'root' })
export class FoxpostDialogService {
  constructor(private dialog: MatDialog) {}

  open(data: FoxpostDialogData) {
    return this.dialog.open<FoxpostDialogComponent, FoxpostDialogData, FoxpostDialogResult>(FoxpostDialogComponent, {
      width: '40rem',
      maxWidth: 'calc(100vw - 1.5rem)',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
      panelClass: 'bgf-label-dialog-panel',
      backdropClass: 'bgf-confirm-dialog-backdrop',
      data,
    }).afterClosed();
  }
}
